// A per-binder Signed-in read: one small Viewer-relative payload per binder
// load (the in-binder star's hits, the Want Overlap), never awaited by the
// page load. Keyed by the binder URL it was fetched for, so a value is never
// selected for another binder; the one slot always belongs to the newest
// request, so a slow response for binder A can never evict B. Skipped for an
// anonymous Viewer, decided on the restored session (isSignedIn), never on a
// timeout: the binder page no longer waits for auth, so these reads must.
import { isSignedIn } from './auth.svelte.js';
import { normalizeToShortUrl } from '../services/recent-binders.js';

/**
 * @template [T=any]
 * @param {(shortUrl: string) => Promise<import('../types.js').ApiResult<any>>} fetchFn
 * @param {(data: any) => T} [toValue] shapes the response data once, when it lands
 */
export function createViewerBinderRead(fetchFn, toValue = (data) => data) {
  const state = $state({
    /** @type {string | null} the binder URL `value` was fetched for */
    binderUrl: null,
    /** @type {T | null} */
    value: null,
  });
  /** @type {string | null} the binder URL of the most recent load */
  let latestRequested = null;

  /**
   * Fetch for `binderUrl` (any form). Anonymous Viewers, unrecognized URLs
   * and failed requests leave the store untouched.
   * @param {string} binderUrl
   */
  async function load(binderUrl) {
    latestRequested = binderUrl;
    if (!(await isSignedIn())) return;
    const shortUrl = normalizeToShortUrl(binderUrl);
    if (!shortUrl) return;
    const res = await fetchFn(shortUrl);
    if (latestRequested !== binderUrl) return; // stale: a newer binder was requested
    if (!res.ok || !res.data) return;
    Object.assign(state, { binderUrl, value: toValue(res.data) }); // one atomic write
  }

  /**
   * The value for the binder on screen, or null when the stored one belongs
   * to another binder (or none was fetched).
   * @param {string | null | undefined} binderUrl
   * @returns {T | null}
   */
  function valueFor(binderUrl) {
    return binderUrl && state.binderUrl === binderUrl ? state.value : null;
  }

  return { state, load, valueFor };
}
